import { Component, OnInit } from '@angular/core';
import { MonthlyPlanService } from '../shared/services/monthly-plan.service';
import { MonthlyPlan } from '../shared/models/monthly-plan';
import { StaticValues } from '@admin/shared/static-values';
import { SelectItem } from 'primeng/api';
import { MonthlyPlanItem } from '../shared/models/monthly-plan-item';
import { ProductType } from '@admin/product-purchases/shared/models/product-type';
import { PortfolioService } from '@admin/portfolios/shared/services/portfolio.service';
import { PortfolioModel } from '@admin/portfolios/shared/models/portfolio-model';
import { CustomMessageService } from '@admin/core/message.service';
import { ApplicationMessageSource } from '@admin/core/models/application-message-source';
import { MessageType } from '@admin/core/models/enum/message-type';
import { ApplicationMessage } from '@admin/core/models/application-message';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-monthly-plan',
  templateUrl: './monthly-plan.component.html',
  styleUrls: ['./monthly-plan.component.less'],
  standalone: false,
})
export class MonthlyPlanComponent implements OnInit {
  public plan: MonthlyPlan;
  public monthFilterOptions: SelectItem[];
  public yearFilterOptions: SelectItem[];
  public portfolios: PortfolioModel[];
  public monthNames: string[];
  public isInCreateMode: boolean;
  public typeItemsMap: { [type: number]: MonthlyPlanItem[] } = {};

  public year: number;
  public month: number;
  public portfolioId: number;

  public ProductTypeEnum = ProductType;

  constructor(
    private monthlyPlanService: MonthlyPlanService,
    private translateService: TranslateService,
    private portfolioService: PortfolioService,
    private messageService: CustomMessageService
  ) {
    this.isInCreateMode = false;
  }

  onSave() {
    if (this.isInCreateMode) {
      this.saveHelper(
        this.monthlyPlanService.createPlan.bind(this.monthlyPlanService)
      );
    } else {
      this.saveHelper(
        this.monthlyPlanService.editPlan.bind(this.monthlyPlanService)
      );
    }
  }

  onChange() {
    this.initPlan(false);
  }

  onCancel() {
    this.isInCreateMode = false;
    this.portfolioId = null;
    this.initPlan(true);
  }

  ngOnInit() {
    this.initYearFilterOptions();
    this.initMonthFilterOptions();
    this.initPortfolioFilterOptions();

    this.initPlan(true);
  }

  private initPlan(current: boolean) {
    this.plan = null;

    if (current) this.initCurrentOrEmptyPlan(this.portfolioId);
    else this.searchPlan(this.year, this.month, this.portfolioId);
  }

  private initCurrentOrEmptyPlan(portfolio: number) {
    this.monthlyPlanService.getCurrentPlan(portfolio).subscribe((res) => {
      if (res != null) {
        this.gotPlanResponse(false, res);
      } else {
        this.monthlyPlanService.getEmptyPlan().subscribe((p) => {
          this.gotPlanResponse(true, p);
        });
      }
    });
  }

  private searchPlan(year: number, month: number, portfolio: number) {
    this.monthlyPlanService.getPlan(year, month, portfolio).subscribe((res) => {
      if (res != null) {
        this.gotPlanResponse(false, res);
      } else {
        this.monthlyPlanService
          .getEmptyPlanSpecific(year, month, portfolio)
          .subscribe((p) => {
            this.gotPlanResponse(true, p);
          });
      }
    });
  }

  private gotPlanResponse(isEmpty: boolean, plan: MonthlyPlan) {
    this.isInCreateMode = isEmpty;
    if (!isEmpty || !this.year) {
      this.year = plan.year;
      this.month = plan.month;
      this.portfolioId = plan.portfolioId;
    }
    this.plan = plan;
    this.initProductsMap();
  }

  private initProductsMap() {
    let sortOrder = [
      ProductType.Announcement_FacebookShare,
      ProductType.Announcement_LinkedInShare,
      ProductType.Announcement_InstagramShare,
      ProductType.Announcement_Vk_share,
      ProductType.Announcment,
      ProductType.Announcement_Renewal,
      ProductType.Announcement_Frame,
      ProductType.Announcement_Logo,
      ProductType.CvDatabaseAccess,
      ProductType.EventCvDatabaseAccess,
      ProductType.CvBox,
      ProductType.SliderBanner,
    ];

    this.plan.monthlyPlanProductTypes.sort(function (a, b) {
      return (
        sortOrder.indexOf(a.productType.productTypeId) -
        sortOrder.indexOf(b.productType.productTypeId)
      );
    });

    this.plan.monthlyPlanProductTypes.forEach((t) => {
      let key =
        t.productType.productTypeId +
        (t.website ? '_' + t.website.providerWebsiteId : '');
      this.typeItemsMap[key] = this.plan.monthlyPlanItems.filter(
        (i) =>
          i.productTypeId == t.productType.productTypeId &&
          (i.website ? i.website.providerWebsiteId : -1) ==
            (t.website ? t.website.providerWebsiteId : -1)
      );
    });
  }

  private initMonthFilterOptions() {
    this.monthNames = StaticValues.monthNames;
    this.monthFilterOptions = [];
    for (var i = 1; i <= 12; i++)
      this.monthFilterOptions.push({
        label: this.translateService.instant('label.' + this.monthNames[i - 1]),
        value: i,
      });
  }

  private initYearFilterOptions() {
    this.yearFilterOptions = [];
    let minYear = 2018;
    let currYear = new Date().getFullYear();
    for (var i = minYear; i <= currYear; i++)
      this.yearFilterOptions.push({ label: i.toString(), value: i });
  }

  private initPortfolioFilterOptions() {
    this.portfolioService.getPortfolios().subscribe((p) => {
      this.portfolios = p.items;
    });
  }

  private saveHelper(func: (plan: MonthlyPlan) => any) {
    this.plan.year = this.year;
    this.plan.month = this.month;
    this.plan.portfolioId = this.portfolioId;

    func(this.plan).subscribe((res) => {
      if (res.success) {
        this.showMessage(res.message, MessageType.Success);
        this.plan = null;
        this.initPlan(false);
      } else {
        this.showMessage(res.message, MessageType.Error);
      }
    });
  }

  private showMessage(message: string, type: MessageType) {
    if (message != null && message.length > 0) {
      this.messageService.pushMessage(<ApplicationMessage>{
        type: type,
        sourceId: ApplicationMessageSource.global,
        summary: '',
        detail: message,
      });
    }
  }
}
